import { buildRoleAwarePrefix } from '@/utils/api';
import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '../models/ServiceTypes';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';

// --- Types for Wealths ---
interface FinancialInstitution {
  AggregatorId: number;
  FinancialInstitutionId: number;
  InstitutionUrl: string;
  IsDisabled: boolean;
  InstitutionId: number;
}

export interface WealthErrorDetails {
  StatusCode: string;
  ActionType: string;
  Message: string;
  IsTemporaryIssue: boolean;
}

interface NetworkWealthRecord {
  WealthId: number;
  HasError: boolean;
  IsUpdating: boolean;
  IsConnecting: boolean;
  Name: string;
  CreatedDate: string; // Consider Date type
  UpdatedDate: string; // Consider Date type
  HasAssociatedFiles: boolean;
  FinancialInstitution?: FinancialInstitution; // Optional based on example (WealthType 2 doesn't have it)
  WealthType: number;
  CanEditWealth: boolean;
  CanEditAccounts: boolean;
  CanDeleteAccounts: boolean;
  ExternalId: number;
  ErrorDetails?: WealthErrorDetails | null;
}

// Transformed type with 'id'
export interface WealthRecord extends NetworkWealthRecord {
  id: number;
}

type NetworkWealthsResponse = Omit<
  RecordResponse<NetworkWealthRecord>,
  'SummedValue'
> & {
  SummedValue?: number | null;
};
export type WealthsResponse = RecordResponse<WealthRecord>;

const isNetworkWealthsResponse = (
  value: unknown,
): value is NetworkWealthsResponse => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'Records' in value &&
    Array.isArray(value.Records) &&
    'TotalLength' in value &&
    typeof value.TotalLength === 'number' &&
    (!('SummedValue' in value) ||
      typeof value.SummedValue === 'number' ||
      value.SummedValue === null)
  );
};

const createCustomError = (message: string): FetchBaseQueryError => ({
  status: 'CUSTOM_ERROR',
  data: { message },
  error: message,
});

// Args type
interface GetWealthsArgs {
  sort?: Array<{
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
  _?: string; // Cache busting parameter
  InvestorId?: number;
}
// --- End Types for Wealths ---

/**
 * API service for wealth-related endpoints
 */
export const wealthsApi = createApi({
  reducerPath: 'wealthsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['Wealths'], // Add Wealths tag type

  endpoints: (builder) => ({
    getWealths: builder.query<WealthsResponse, GetWealthsArgs | undefined>({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Base URL with role-aware + optional investor scoping
        const url = `${buildRoleAwarePrefix(currentRole, args?.InvestorId)}/wealths`;

        const params: Record<string, string | number> = {};

        // Handle sort parameters
        if (args?.sort && args.sort.length > 0) {
          args.sort.forEach((sortItem, index) => {
            const i = String(index);
            params[`sort[${i}].Property`] = sortItem.property;
            params[`sort[${i}].Direction`] = sortItem.direction;
            params[`sort[${i}].Order`] = sortItem.order;
          });
        }

        params._ = args?._ ?? Date.now();
        // No extra query param required for investor ID here beyond prefix

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            params,
          });

          const responseData = result.data;
          const hasValidResponse = isNetworkWealthsResponse(responseData);
          console.log('WEALTHS API RESULT:', {
            isError: !!result.error,
            hasData: !!result.data,
            paramsSent: params,
            urlSent: url,
            dataLength: hasValidResponse ? responseData.Records.length : 'N/A',
            totalLength: hasValidResponse ? responseData.TotalLength : 'N/A',
            resultMeta: {
              status: result.meta?.response?.status,
              statusText: result.meta?.response?.statusText,
            },
          });

          if (result.error) {
            console.error('WEALTHS API ERROR', result.error);
            return { error: result.error };
          }

          if (!result.data) {
            console.error('WEALTHS API RETURNED NO DATA');
            return {
              error: createCustomError('No data returned from API'),
            };
          }

          // Basic check for expected structure
          if (!hasValidResponse) {
            console.warn('WEALTHS API RETURNED UNEXPECTED DATA STRUCTURE');
            return {
              error: createCustomError('Unexpected data structure'),
            };
          }

          // --- Transformation Step ---
          const transformedRecords: WealthRecord[] = responseData.Records.map(
            (wealth) => ({
              ...wealth,
              id: wealth.WealthId, // Map WealthId to id
            }),
          );

          const transformedData: WealthsResponse = {
            SummedValue: responseData.SummedValue ?? null,
            TotalLength: responseData.TotalLength,
            Records: transformedRecords,
          };
          // --- End Transformation Step ---

          return { data: transformedData }; // Return the transformed data
        } catch (err) {
          console.error('WEALTHS API EXCEPTION:', err);
          return {
            error: createCustomError(String(err)),
          };
        }
      },
      providesTags: (result) =>
        result?.Records
          ? [
              ...result.Records.map(({ id }) => ({
                type: 'Wealths' as const,
                id: id,
              })),
              { type: 'Wealths', id: 'LIST' },
            ]
          : [{ type: 'Wealths', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

export const { useGetWealthsQuery } = wealthsApi;
